import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  REFERENCE_CATEGORY_LABELS,
  REFERENCE_DESCRIPTION_MAX,
  REFERENCE_NOTE_MAX,
  REFERENCE_SOURCE_LABELS,
  REFERENCE_TITLE_MAX,
  type ReferenceDetail,
  type ReferenceGroupView,
  type UpdateReferenceInput,
} from '@scoutaround/script-breakdown-shared';

import { api, ApiError } from '../../lib/api.js';
import { useCan, useRefusal } from '../../lib/permissions.js';
import { Icon } from '../Icon.js';
import { MoreMenu } from '../ui/MoreMenu.js';
import { EmptyState } from '../ui/EmptyState.js';
import { ThreadList } from '../conversations/ThreadList.js';
import { NewThreadForm } from '../conversations/NewThreadForm.js';
import { useConversationsEnabled, useDiscuss } from '../conversations/hooks.js';
import { CategorySelect, GroupChooser, LinksEditor, TagEditor, type EditableLink } from './Pickers.js';
import {
  absoluteTime,
  fileKindLabel,
  formatBytes,
  hostOf,
  personLabel,
  referenceIcon,
  relativeTime,
} from './format.js';
import { errorText, referencesKey, useInvalidateReferences, useReferenceDetail } from './hooks.js';

/** The address of one reference, to copy and send. */
export function referenceShareUrl(projectId: string, referenceId: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  return `${origin}/projects/${projectId}/references?ref=${encodeURIComponent(referenceId)}`;
}

const CHANGED_ELSEWHERE =
  'Someone changed this reference while you were editing. It now shows their version — make your change again if it is still needed.';

/**
 * ONE REFERENCE, OPEN (`?ref=`): the large re-encoded preview, its words edited
 * where they stand, how it is organised, what it is linked to, and its
 * discussions — which are ordinary conversations anchored to it, opened in the
 * workspace's own conversation drawer.
 *
 * Every edit is a compare-and-swap on `rowVersion`. A stale write (409
 * `REFERENCE_CHANGED`) re-reads the reference and says so; it never overwrites
 * somebody else's change silently, and never keeps the stale text on screen as
 * if it had saved.
 */
export function ReferenceDetailPanel({
  projectId,
  referenceId,
  groups,
  tagSuggestions,
  onClose,
  onDeleted,
}: {
  projectId: string;
  referenceId: string;
  groups: readonly ReferenceGroupView[];
  tagSuggestions: readonly string[];
  onClose: () => void;
  onDeleted: () => void;
}) {
  const detail = useReferenceDetail(projectId, referenceId);
  const headingRef = useRef<HTMLHeadingElement>(null);

  // A newly opened reference takes the reader's attention (on a phone it IS the screen).
  const loaded = detail.data !== undefined;
  useEffect(() => {
    if (loaded) headingRef.current?.focus({ preventScroll: true });
  }, [referenceId, loaded]);

  if (detail.isPending) {
    return (
      <div className="ref-detail-inner" aria-busy="true" data-testid="ref-detail-loading">
        <DetailBack onClose={onClose} />
        <div className="ref-detail-head">
          <span className="ref-skeleton-line" style={{ width: '70%', height: 18 }} />
          <CloseButton onClose={onClose} />
        </div>
        <div className="ref-detail-preview ref-skeleton" />
        <span className="ref-skeleton-line" style={{ width: '45%' }} />
      </div>
    );
  }

  if (detail.isError) {
    const gone = detail.error instanceof ApiError && detail.error.status === 404;
    return (
      <div className="ref-detail-inner" data-testid="ref-detail-error">
        <DetailBack onClose={onClose} />
        <div className="ref-detail-head">
          <span />
          <CloseButton onClose={onClose} />
        </div>
        <EmptyState
          title={gone ? 'This reference is not in the library' : 'The reference could not be loaded'}
          action={
            gone ? undefined : (
              <button
                type="button"
                className="ctl"
                data-testid="ref-detail-retry"
                onClick={() => {
                  void detail.refetch();
                }}
              >
                Try again
              </button>
            )
          }
        >
          {gone ? 'It may have been deleted, or the link belongs to another production.' : errorText(detail.error, '')}
        </EmptyState>
      </div>
    );
  }

  return (
    <LoadedDetail
      projectId={projectId}
      reference={detail.data}
      groups={groups}
      tagSuggestions={tagSuggestions}
      headingRef={headingRef}
      onClose={onClose}
      onDeleted={onDeleted}
    />
  );
}

/** A phone's way back to the list; the open reference fills the screen there. */
function DetailBack({ onClose }: { onClose: () => void }) {
  return (
    <div className="ref-detail-back-row">
      <button type="button" className="ref-detail-back" data-testid="ref-detail-back" onClick={onClose}>
        <Icon name="arrowLeft" size={14} />
        All references
      </button>
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      className="ctl ref-icon-btn ref-detail-close"
      aria-label="Close the reference"
      data-testid="ref-detail-close"
      onClick={onClose}
    >
      <Icon name="close" size={16} />
    </button>
  );
}

function LoadedDetail({
  projectId,
  reference: r,
  groups,
  tagSuggestions,
  headingRef,
  onClose,
  onDeleted,
}: {
  projectId: string;
  reference: ReferenceDetail;
  groups: readonly ReferenceGroupView[];
  tagSuggestions: readonly string[];
  headingRef: React.RefObject<HTMLHeadingElement | null>;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateReferences(projectId);
  const canEdit = useCan('REFERENCE_EDIT');
  // A saved location from the old board keeps its title, text and category (the
  // server refuses a PATCH, REFERENCE_READ_ONLY); its groups, tags and links still change.
  const canEditFields = canEdit && r.sourceType !== 'LOCATION';
  const canRemove = useCan('REFERENCE_REMOVE');
  const editRefusal = useRefusal('REFERENCE_EDIT');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const detailKey = [...referencesKey(projectId), 'detail', r.id];
  const groupsLabel = useId();

  const accept = async (fresh: ReferenceDetail) => {
    // The PATCH answer carries no large preview; keep the one already on screen.
    queryClient.setQueryData<ReferenceDetail>(detailKey, (old) =>
      old && fresh.preview.largeUrl === null && old.preview.status === fresh.preview.status
        ? { ...fresh, preview: { ...fresh.preview, largeUrl: old.preview.largeUrl } }
        : fresh,
    );
    // Everything else that shows this reference (the list, the counts) re-reads.
    await queryClient.invalidateQueries({
      queryKey: referencesKey(projectId),
      predicate: (q) => q.queryKey[2] !== 'detail',
    });
  };

  const failed = async (e: unknown, fallback: string) => {
    if (e instanceof ApiError && e.code === 'REFERENCE_READ_ONLY') {
      setError('A saved location from the old board cannot be edited. Its groups, tags and links can still change.');
      return;
    }
    if (e instanceof ApiError && e.code === 'PREVIEW_NOT_RETRYABLE') {
      setError(errorText(e, 'This preview cannot be made again.'));
      return;
    }
    if (e instanceof ApiError && (e.code === 'REFERENCE_CHANGED' || e.status === 409)) {
      setError(null);
      setNotice(CHANGED_ELSEWHERE);
      await queryClient.invalidateQueries({ queryKey: detailKey });
      return;
    }
    setError(errorText(e, fallback));
  };

  // The version a save compares against is the one the person was looking at when
  // they started (InlineText captures it at Edit), never the one a background re-read
  // put on screen while they typed: that would overwrite a colleague's change unseen.
  const patch = useMutation({
    mutationFn: ({ expectedVersion, ...change }: Omit<UpdateReferenceInput, 'expectedVersion'> & { expectedVersion?: number }) =>
      api.updateReference(projectId, r.id, { expectedVersion: expectedVersion ?? r.rowVersion, ...change }),
    onSuccess: async (fresh) => {
      setNotice(null);
      setError(null);
      await accept(fresh);
    },
    onError: (e) => failed(e, 'The change could not be saved.'),
  });

  const setGroups = useMutation({
    mutationFn: (groupIds: string[]) => api.setReferenceGroups(projectId, r.id, groupIds),
    onSuccess: accept,
    onError: (e) => failed(e, 'The groups could not be saved.'),
  });

  const setTags = useMutation({
    mutationFn: (tags: string[]) => api.setReferenceTags(projectId, r.id, tags),
    onSuccess: accept,
    onError: (e) => failed(e, 'The tags could not be saved.'),
  });

  const linkWrite = useMutation({
    mutationFn: (run: () => Promise<unknown>) => run(),
    onSuccess: async () => {
      setError(null);
      await invalidate();
    },
    onError: (e) => {
      setError(errorText(e, 'The link could not be changed.'));
    },
  });

  const retryPreview = useMutation({
    mutationFn: () => api.retryReferencePreview(projectId, r.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: detailKey });
      await invalidate();
    },
    onError: (e) => {
      setError(errorText(e, 'The preview could not be made again.'));
    },
  });

  const remove = useMutation({
    mutationFn: () => api.removeReference(projectId, r.id),
    onSuccess: async () => {
      setConfirmDelete(false);
      onDeleted();
      await invalidate();
    },
    onError: (e) => {
      setError(errorText(e, 'The reference could not be deleted.'));
    },
  });

  const busy = patch.isPending || setGroups.isPending || setTags.isPending;

  const copyLink = async () => {
    const url = referenceShareUrl(projectId, r.id);
    try {
      await navigator.clipboard.writeText(url);
      setCopied('Link copied.');
    } catch {
      setCopied(`Copy this link: ${url}`);
    }
  };

  const links: EditableLink[] = r.links.map((l) => ({
    key: l.id,
    targetType: l.targetType,
    targetId: l.targetId,
    label: l.label,
    removed: l.state === 'REMOVED',
  }));

  return (
    <article className="ref-detail-inner" aria-labelledby={`ref-detail-title-${r.id}`} data-testid={`ref-detail-${r.id}`}>
      <DetailBack onClose={onClose} />
      <div className="ref-detail-head">
        <div className="ref-detail-head-main">
          <InlineText
            value={r.title}
            label="Title"
            editable={canEditFields}
            required
            maxLength={REFERENCE_TITLE_MAX}
            busy={patch.isPending}
            testId="ref-detail-title"
            render={(text) => (
              <h2 id={`ref-detail-title-${r.id}`} ref={headingRef} tabIndex={-1} className="ref-detail-title">
                {text}
              </h2>
            )}
            version={r.rowVersion}
            onSave={(title, expectedVersion) => {
              patch.mutate({ title, expectedVersion });
            }}
          />
        </div>
        <div className="ref-detail-top-actions">
          <MoreMenu
            label="Reference actions"
            testId="ref-detail-menu"
            items={[
              {
                id: 'copy',
                label: 'Copy link',
                testId: 'ref-detail-copy',
                onSelect: () => {
                  void copyLink();
                },
              },
              ...(canRemove
                ? [
                    {
                      id: 'delete',
                      label: 'Delete reference',
                      tone: 'danger' as const,
                      testId: 'ref-detail-delete',
                      onSelect: () => {
                        setConfirmDelete(true);
                      },
                    },
                  ]
                : []),
            ]}
          />
          <CloseButton onClose={onClose} />
        </div>
      </div>

      {copied !== null ? (
        <p className="ref-quiet ref-copied" role="status" data-testid="ref-detail-copied">
          {copied}
        </p>
      ) : null}

      {!canEdit && editRefusal !== null ? (
        <p className="ref-quiet" data-testid="ref-detail-readonly">
          {editRefusal}
        </p>
      ) : null}

      {notice !== null ? (
        <p className="ref-notice" role="status" data-testid="ref-detail-changed">
          {notice}
        </p>
      ) : null}
      {error !== null ? (
        <p className="ref-error" role="alert" data-testid="ref-detail-error-message">
          {error}
        </p>
      ) : null}

      <Preview reference={r} canEdit={canEdit} retrying={retryPreview.isPending} onRetry={() => { retryPreview.mutate(); }} />

      {r.sourceType === 'LINK' && r.url ? (
        <p className="ref-detail-url" data-testid="ref-detail-url">
          <Icon name="link" size={14} />
          <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid="ref-detail-url-link">
            {r.siteName ? `${r.siteName} · ` : ''}
            {r.domain ?? hostOf(r.url) ?? r.url}
            <span className="sr-only"> (opens in a new tab)</span>
          </a>
          <Icon name="external" size={13} />
        </p>
      ) : null}

      {r.file ? (
        <div className="ref-file-facts" data-testid="ref-detail-file">
          <p className="ref-file-name">{r.file.originalFilename}</p>
          <p className="ref-quiet">
            {`${fileKindLabel(r.file.kind)} · ${formatBytes(r.file.byteSize)} · ${r.file.mimeType}`}
          </p>
          <p className="ref-quiet" data-testid="ref-detail-file-private">
            The original file is kept privately for this production and cannot be opened or downloaded here yet. Only
            the preview above, made by ScoutAround from the file, is shown.
          </p>
        </div>
      ) : null}

      {r.sourceType !== 'NOTE' && r.note ? (
        <section className="ref-detail-section" aria-label="Why it was saved" data-testid="ref-detail-legacy-note">
          <p className="flbl">Why it was saved</p>
          <p className="ref-detail-note">{r.note}</p>
        </section>
      ) : null}

      {r.sourceType === 'NOTE' ? (
        <section className="ref-detail-section" aria-label="Note">
          <p className="flbl">Note</p>
          <InlineText
            value={r.note ?? ''}
            label="Note"
            multiline
            editable={canEdit}
            required
            maxLength={REFERENCE_NOTE_MAX}
            busy={patch.isPending}
            testId="ref-detail-note"
            render={(text) => <p className="ref-detail-note">{text}</p>}
            version={r.rowVersion}
            onSave={(note, expectedVersion) => {
              patch.mutate({ note, expectedVersion });
            }}
          />
        </section>
      ) : null}

      <section className="ref-detail-section" aria-label="Description">
        <p className="flbl">{r.sourceType === 'NOTE' ? 'Description' : 'Saved note'}</p>
        <InlineText
          value={r.description ?? ''}
          label="Description"
          multiline
          editable={canEditFields}
          maxLength={REFERENCE_DESCRIPTION_MAX}
          placeholder="Add why it was saved, what to look at"
          busy={patch.isPending}
          testId="ref-detail-description"
          render={(text) => <p className="ref-detail-description">{text}</p>}
          version={r.rowVersion}
          onSave={(description, expectedVersion) => {
            patch.mutate({ description: description === '' ? null : description, expectedVersion });
          }}
        />
      </section>

      <div className="ref-detail-grid">
        <div className="ref-field">
          <label className="flbl" htmlFor={`ref-detail-category-${r.id}`}>
            Category
          </label>
          {canEditFields ? (
            <CategorySelect
              id={`ref-detail-category-${r.id}`}
              value={r.category}
              disabled={busy}
              onChange={(category) => {
                patch.mutate({ category });
              }}
              testId="ref-detail-category"
            />
          ) : (
            <p className="fval" id={`ref-detail-category-${r.id}`} data-testid="ref-detail-category">
              {r.category === null ? 'Not set' : REFERENCE_CATEGORY_LABELS[r.category]}
            </p>
          )}
        </div>
        <div className="ref-field">
          <p className="flbl" id={groupsLabel}>
            Groups / Themes
          </p>
          <GroupChooser
            groups={groups.length > 0 ? groups : r.groups.map((g) => ({ ...g, count: 0 }))}
            value={r.groups.map((g) => g.id)}
            disabled={!canEdit || busy}
            onChange={(next) => {
              setGroups.mutate(next);
            }}
            testId="ref-detail-groups"
            labelId={groupsLabel}
          />
        </div>
      </div>

      <section className="ref-detail-section" aria-label="Tags">
        <p className="flbl">Tags</p>
        <TagEditor
          tags={r.tags}
          suggestions={tagSuggestions}
          disabled={!canEdit}
          onChange={(next) => {
            setTags.mutate(next);
          }}
          testId="ref-detail-tags"
        />
      </section>

      <section className="ref-detail-section" aria-label="Linked to">
        <p className="flbl">Linked to</p>
        {links.length === 0 && !canEdit ? <p className="ref-quiet">Not linked to anything in the production.</p> : null}
        <LinksEditor
          projectId={projectId}
          links={links}
          editable={canEdit}
          busy={linkWrite.isPending}
          onAdd={(option) => {
            linkWrite.mutate(() =>
              api.addReferenceLink(projectId, r.id, { targetType: option.targetType, targetId: option.targetId }),
            );
          }}
          onRemove={(link) => {
            linkWrite.mutate(() => api.removeReferenceLink(projectId, r.id, link.key));
          }}
          testId="ref-detail-links"
        />
      </section>

      <Discussions projectId={projectId} reference={r} />

      <p className="ref-detail-meta" data-testid="ref-detail-meta">
        {`${REFERENCE_SOURCE_LABELS[r.sourceType]} · added by ${personLabel(r.createdBy.displayName)} `}
        <time dateTime={r.createdAt} title={absoluteTime(r.createdAt)}>
          {relativeTime(r.createdAt)}
        </time>
        {r.updatedAt !== r.createdAt ? (
          <>
            {' · updated '}
            <time dateTime={r.updatedAt} title={absoluteTime(r.updatedAt)}>
              {relativeTime(r.updatedAt)}
            </time>
          </>
        ) : null}
      </p>

      {confirmDelete ? (
        <div className="ref-confirm" role="alertdialog" aria-labelledby="ref-delete-title" aria-describedby="ref-delete-body" data-testid="ref-detail-delete-confirm">
          <p id="ref-delete-title" className="ref-confirm-title">
            Delete this reference?
          </p>
          <p id="ref-delete-body" className="ref-confirm-body">
            {`“${r.title}” leaves the library for everyone on this production. Its discussions stay readable in Conversations.`}
          </p>
          <div className="ref-confirm-actions">
            <button
              type="button"
              className="ctl"
              autoFocus
              data-testid="ref-detail-delete-cancel"
              onClick={() => {
                setConfirmDelete(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn ref-danger-btn"
              disabled={remove.isPending}
              data-testid="ref-detail-delete-submit"
              onClick={() => {
                remove.mutate();
              }}
            >
              {remove.isPending ? 'Deleting…' : 'Delete reference'}
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function Preview({
  reference: r,
  canEdit,
  retrying,
  onRetry,
}: {
  reference: ReferenceDetail;
  canEdit: boolean;
  retrying: boolean;
  onRetry: () => void;
}) {
  const url = r.preview.largeUrl ?? r.preview.thumbUrl;
  if (r.preview.status === 'READY' && url !== null) {
    return (
      <div className="ref-detail-preview" data-testid="ref-detail-preview">
        <img
          src={url}
          alt={`Preview of ${r.title}`}
          width={r.preview.width ?? undefined}
          height={r.preview.height ?? undefined}
          data-testid="ref-detail-preview-image"
        />
      </div>
    );
  }
  // A note is its text and needs no picture; a link without one says nothing.
  if (
    r.sourceType === 'NOTE' ||
    r.sourceType === 'LOCATION' ||
    (r.sourceType === 'LINK' && r.preview.status === 'NONE')
  ) {
    return null;
  }

  // A server older than the SPA sends no code; that preview reads as simply failed.
  const message =
    r.preview.status === 'FAILED'
      ? r.preview.failureCode === 'RATE_LIMITED'
        ? 'The website asked us to slow down. Retry the preview in a minute.'
        : 'The preview could not be made.'
      : r.preview.status === 'UNSUPPORTED'
        ? 'No preview for this kind of file.'
        : 'No preview yet.';

  return (
    <div className="ref-detail-preview" data-state={r.preview.status} data-testid="ref-detail-preview-placeholder">
      <span className="ref-detail-preview-icon">
        <Icon name={referenceIcon(r)} size={40} strokeWidth={1.3} />
      </span>
      <p className="ref-quiet" data-testid={`ref-detail-preview-${r.preview.status.toLowerCase()}`}>
        {message}
      </p>
      {r.preview.status === 'FAILED' && canEdit && r.file?.kind !== 'VIDEO' ? (
        <button type="button" className="ctl" disabled={retrying} data-testid="ref-detail-preview-retry" onClick={onRetry}>
          {retrying ? 'Trying again…' : 'Retry preview'}
        </button>
      ) : null}
    </div>
  );
}

/**
 * A field read as text and edited where it stands. Enter saves a single line,
 * Escape puts the text back; a required field refuses to be emptied.
 */
function InlineText({
  value,
  label,
  editable,
  multiline = false,
  required = false,
  maxLength,
  placeholder,
  busy,
  testId,
  render,
  version,
  onSave,
}: {
  value: string;
  label: string;
  editable: boolean;
  multiline?: boolean;
  required?: boolean;
  maxLength: number;
  placeholder?: string;
  busy: boolean;
  testId: string;
  render: (text: string) => React.ReactNode;
  /** The reference's `rowVersion` now; the one held at Edit is what the save compares against. */
  version: number;
  onSave: (next: string, expectedVersion: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const startedAt = useRef(version);
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);
  const inputId = useId();

  // A re-read (another person's change, a 409) replaces what is shown.
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  if (!editing) {
    if (!editable) {
      return value === '' ? (
        <p className="ref-quiet" data-testid={testId}>
          Nothing yet.
        </p>
      ) : (
        <div data-testid={testId}>{render(value)}</div>
      );
    }
    return (
      <div className="ref-inline" data-testid={testId}>
        {value === '' ? <p className="ref-quiet">{placeholder ?? 'Nothing yet.'}</p> : render(value)}
        <button
          type="button"
          className="ref-text-btn"
          aria-label={`Edit ${label.toLowerCase()}`}
          data-testid={`${testId}-edit`}
          onClick={() => {
            setDraft(value);
            setInvalid(false);
            startedAt.current = version;
            setEditing(true);
          }}
        >
          <Icon name="pencil" size={13} />
          <span aria-hidden="true">Edit</span>
        </button>
      </div>
    );
  }

  const save = () => {
    const next = multiline ? draft.replace(/\s+$/, '') : draft.trim();
    if (required && next.trim() === '') {
      setInvalid(true);
      return;
    }
    setEditing(false);
    if (next !== value) onSave(next, startedAt.current);
  };

  return (
    <form
      className="ref-inline-form"
      data-testid={`${testId}-form`}
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          setEditing(false);
          setDraft(value);
        }
      }}
    >
      <label className="sr-only" htmlFor={inputId}>
        {label}
      </label>
      {multiline ? (
        <textarea
          id={inputId}
          className="ref-textarea"
          rows={4}
          autoFocus
          value={draft}
          maxLength={maxLength}
          aria-invalid={invalid}
          data-testid={`${testId}-input`}
          onChange={(event) => {
            setDraft(event.target.value);
            setInvalid(false);
          }}
        />
      ) : (
        <input
          id={inputId}
          className="ref-input"
          autoFocus
          value={draft}
          maxLength={maxLength}
          aria-invalid={invalid}
          data-testid={`${testId}-input`}
          onChange={(event) => {
            setDraft(event.target.value);
            setInvalid(false);
          }}
        />
      )}
      {invalid ? (
        <p className="ref-error" role="alert">
          {`${label} cannot be empty.`}
        </p>
      ) : null}
      <div className="ref-inline-actions">
        <button type="submit" className="ctl" data-on="1" disabled={busy} data-testid={`${testId}-save`}>
          Save
        </button>
        <button
          type="button"
          className="ctl"
          data-testid={`${testId}-cancel`}
          onClick={() => {
            setEditing(false);
            setDraft(value);
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

/**
 * THE REFERENCE'S DISCUSSIONS ARE CONVERSATIONS anchored to it (design §1).
 * Listed here, started here with the workspace's own form, and read in the
 * workspace's own drawer — there is no second message renderer.
 */
function Discussions({ projectId, reference: r }: { projectId: string; reference: ReferenceDetail }) {
  const enabled = useConversationsEnabled();
  if (!enabled) return null;
  return <EnabledDiscussions projectId={projectId} reference={r} />;
}

function EnabledDiscussions({ projectId, reference: r }: { projectId: string; reference: ReferenceDetail }) {
  const { open } = useDiscuss();
  const invalidateLibrary = useInvalidateReferences(projectId);
  const [starting, setStarting] = useState(false);
  const canPost = useCan('CONVERSATION_POST');
  const threads = useQuery({
    queryKey: ['conversations', projectId, 'anchor', 'REFERENCE', r.id],
    queryFn: () => api.conversations(projectId, { anchorType: 'REFERENCE', anchorId: r.id }),
    refetchInterval: 30_000,
  });
  const list = threads.data?.threads ?? [];
  const target = { anchorType: 'REFERENCE' as const, anchorId: r.id, label: r.title };

  return (
    <section className="ref-detail-section ref-discussion" aria-labelledby={`ref-discussion-${r.id}`} data-testid="ref-detail-discussions">
      <div className="ref-discussion-head">
        <h3 id={`ref-discussion-${r.id}`} className="ref-discussion-title">
          <Icon name="chat" size={16} />
          {threads.data ? `Discussion (${String(list.length)})` : 'Discussion'}
        </h3>
        {!starting && canPost ? (
          <button
            type="button"
            className="ctl"
            data-testid="ref-detail-start-discussion"
            onClick={() => {
              setStarting(true);
            }}
          >
            Start discussion
          </button>
        ) : null}
      </div>
      {starting ? (
        <NewThreadForm
          projectId={projectId}
          anchorType="REFERENCE"
          anchorId={r.id}
          onCancel={() => {
            setStarting(false);
          }}
          onCreated={(thread) => {
            setStarting(false);
            // The card's count and the "With discussion" chip are the library's answer.
            void invalidateLibrary();
            open(target, thread.id);
          }}
        />
      ) : null}
      {threads.isPending ? <p className="ref-quiet">Loading…</p> : null}
      {threads.isError ? (
        <p className="ref-quiet" role="alert">
          {errorText(threads.error, 'The discussions could not be loaded.')}
        </p>
      ) : null}
      {threads.data && !starting ? (
        <ThreadList
          testId="ref-detail-thread-list"
          threads={list}
          showAnchor={false}
          emptyText="No discussions yet. Start one to talk this reference over with the team."
          onOpen={(threadId) => {
            open(target, threadId);
          }}
        />
      ) : null}
    </section>
  );
}
